import Phaser from 'phaser';
import {
  MAX_CHAPTERS,
  chapterLevelLabel,
  findBorder,
  isLocked,
  onMapPlots,
  publishState,
  type CreatorChapter,
  type CreatorProject,
  type PublishedRow,
} from '../../config/creator';
import { accountClient } from '../../account/accountUi';
import {
  askUnban,
  askUnbanChapter,
  cancelChapterRequest,
  cancelLevelRequest,
  deleteChapter,
  myChapters,
  myPublished,
  myPublishedChapters,
  publishChapter,
  publishLevel,
} from '../../account/explore';
import { HUB_MENU_TRACK } from '../../config/levels';
import { LOAD_FAILED } from '../../account/creatorStore';
import { creatorIsDeviceOnly, creatorStoreNow } from '../creator/storeAccess';
import { ScrollList } from '../../ui/scrollList';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import {
  CREATOR_ORANGE,
  CREATOR_PANEL,
  CREATOR_TEAL,
  creatorButton,
  creatorFlash,
  creatorHeader,
  projectThumb,
} from '../creator/creatorUi';

export const EMPTY_MENU_TEXT = 'Click on the build button to get started';
export const EMPTY_CHAPTERS_TEXT = 'Click on the build button to make a chapter';
export const CHAPTERS_SIGN_IN =
  'Sign in to make chapters — every level in a chapter must be published';
export const DEVICE_ONLY_NOTE =
  'Saved on this device — sign in to keep your levels on your account';

const COLS = 3;
const CARD_W = 340;
const CARD_H = 270;
const GAP_X = 30;
const GAP_Y = 24;
const LIST_X = 85;
const LIST_Y = 160;
const LIST_H = 540;

export const PUBLISH_CONFIRM = 'Are you sure?';
export const SIGN_IN_TO_PUBLISH = 'Sign in to publish';
export const LOCKED_TEXT = 'Locked while an admin reviews your unban request';

export type CreatorTab = 'levels' | 'chapters';

type Call = () => Promise<{ ok: boolean; error?: string }>;

/**
 * The creator menu (spec 2026-11-01, 2026-11-03 §5): two tabs. LEVELS holds
 * every level the player has made; CHAPTERS the chapters they have strung
 * together from their published levels (signed in only). BUILD top-right
 * makes a new one of whichever tab is open. Tapping a card opens it; its ✕
 * deletes it (two presses). Under each card is its publishing control (spec
 * 2026-11-02 §4.1): PUBLISH → "Are you sure?" → PENDING ✕; LIVE #CODE +
 * UPDATE; BANNED + ASK; BANNED · ASKED ✕ (locked).
 */
export class CreatorMenuScene extends Phaser.Scene {
  private list?: ScrollList;
  private projects: CreatorProject[] = [];
  private chapters: CreatorChapter[] = [];
  private armedDelete: string | null = null;
  /** The server's view of this player's levels / chapters (empty when device-only). */
  private published = new Map<string, PublishedRow>();
  private publishedChapters = new Map<string, PublishedRow>();
  private deviceOnly = true;
  private confirm: Phaser.GameObjects.GameObject[] = [];
  private busy = false;
  private tab: CreatorTab = 'levels';
  private loadSeq = 0;
  private tabButtons: {
    tab: CreatorTab;
    bg: Phaser.GameObjects.Rectangle;
    text: Phaser.GameObjects.Text;
  }[] = [];

  constructor() {
    super({ key: 'CreatorMenuScene' });
  }

  init(data: { tab?: CreatorTab }): void {
    this.tab = data?.tab === 'chapters' ? 'chapters' : 'levels';
    this.sys.settings.data = {};
  }

  create(): void {
    drawStarfield(this, 1280, 720);
    creatorHeader(this, 'MY LEVELS', () => this.scene.start('HubScene', { tab: 'explore' }));
    creatorButton(
      this,
      1150,
      50,
      'BUILD',
      { name: 'creator-build', fill: CREATOR_ORANGE, width: 160, height: 56, fontSize: 26 },
      () => this.build(),
    );
    this.tabButtons = [];
    this.buildTabs();
    void creatorIsDeviceOnly().then((device) => {
      if (!device || !this.sys.isActive()) return;
      this.add
        .text(640, 136, DEVICE_ONLY_NOTE, {
          fontSize: '15px',
          color: '#9aa6ad',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setName('creator-device-note');
    });

    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.list?.destroy();
      this.list = undefined;
    });

    this.armedDelete = null;
    this.published = new Map();
    this.publishedChapters = new Map();
    this.confirm = [];
    this.busy = false;
    void this.loadData();
  }

  private build(): void {
    if (this.tab === 'levels') {
      if (this.projects.length >= 20) {
        creatorFlash(this, 'You can keep 20 levels — delete one first');
        return;
      }
      this.scene.start('CreatorSettingsScene', {});
      return;
    }
    if (this.deviceOnly || !accountClient()) {
      creatorFlash(this, CHAPTERS_SIGN_IN);
      return;
    }
    if (this.chapters.length >= MAX_CHAPTERS) {
      creatorFlash(this, `You can keep ${MAX_CHAPTERS} chapters — delete one first`);
      return;
    }
    this.scene.start('CreatorChapterScene', {});
  }

  private buildTabs(): void {
    const tabs: [CreatorTab, string, number][] = [
      ['levels', 'LEVELS', 540],
      ['chapters', 'CHAPTERS', 740],
    ];
    for (const [tab, label, x] of tabs) {
      const bg = this.add
        .rectangle(x, 100, 180, 40, CREATOR_PANEL, 1)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-tab-${tab}`);
      const text = this.add
        .text(x, 100, label, { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setLetterSpacing(2);
      bg.on('pointerup', () => {
        if (this.tab === tab || this.confirm.length > 0) return;
        this.tab = tab;
        this.armedDelete = null;
        this.restyleTabs();
        void this.loadData();
      });
      this.tabButtons.push({ tab, bg, text });
    }
    this.restyleTabs();
  }

  private restyleTabs(): void {
    for (const b of this.tabButtons) {
      const on = b.tab === this.tab;
      b.bg.setFillStyle(on ? CREATOR_TEAL : CREATOR_PANEL, 1).setStrokeStyle(2, CREATOR_TEAL, 1);
      b.text.setColor(on ? '#ffffff' : '#9fe0d6');
    }
  }

  private clearList(): void {
    this.list?.destroy();
    this.list = undefined;
    for (const n of ['creator-empty', 'creator-load-failed']) this.children.getByName(n)?.destroy();
  }

  private async loadData(): Promise<void> {
    const seq = ++this.loadSeq;
    this.clearList();
    try {
      this.deviceOnly = await creatorIsDeviceOnly();
      if (this.tab === 'levels') {
        this.projects = await (await creatorStoreNow()).list();
        await this.loadPublished();
      } else {
        await this.loadChapters();
      }
    } catch (e) {
      if (!this.sys.isActive() || seq !== this.loadSeq) return;
      // Never "no levels" on a failed read: say so, and offer a retry.
      this.clearList();
      this.add
        .text(640, 400, `${e instanceof Error ? e.message : LOAD_FAILED}\n\nTap to try again`, {
          fontSize: '24px',
          color: '#ffcf6b',
          fontStyle: 'bold',
          align: 'center',
        })
        .setOrigin(0.5)
        .setName('creator-load-failed')
        .setInteractive({ useHandCursor: true })
        .once('pointerup', () => void this.loadData());
      return;
    }
    if (!this.sys.isActive() || seq !== this.loadSeq) return;
    this.render();
  }

  /** The publish states; a failed read just shows no controls' states. */
  private async loadPublished(): Promise<void> {
    this.published = new Map();
    const client = accountClient();
    if (this.deviceOnly || !client) return;
    const r = await myPublished(client);
    if (r.ok) for (const row of r.value) this.published.set(row.sourceId, row);
  }

  private async loadChapters(): Promise<void> {
    this.chapters = [];
    this.publishedChapters = new Map();
    const client = accountClient();
    if (this.deviceOnly || !client) return;
    const [c, p] = await Promise.all([myChapters(client), myPublishedChapters(client)]);
    if (!c.ok) throw new Error(c.error);
    this.chapters = c.value;
    if (p.ok) for (const row of p.value) this.publishedChapters.set(row.sourceId, row);
  }

  private render(): void {
    this.clearList();
    const chapters = this.tab === 'chapters';
    const count = chapters ? this.chapters.length : this.projects.length;
    if (chapters && (this.deviceOnly || !accountClient())) {
      this.add
        .text(640, 400, CHAPTERS_SIGN_IN, { fontSize: '26px', color: '#e8f4f1', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setName('creator-empty');
      return;
    }
    if (count === 0) {
      this.add
        .text(640, 400, chapters ? EMPTY_CHAPTERS_TEXT : EMPTY_MENU_TEXT, {
          fontSize: '30px',
          color: '#e8f4f1',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setName('creator-empty');
      return;
    }
    const list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_Y,
      width: COLS * CARD_W + (COLS - 1) * GAP_X,
      height: LIST_H,
      depth: 1,
    });
    this.list = list;
    for (let i = 0; i < count; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = col * (CARD_W + GAP_X) + CARD_W / 2;
      const cy = row * (CARD_H + GAP_Y) + CARD_H / 2;
      if (chapters) this.chapterCard(this.chapters[i], i, cx, cy, list);
      else this.levelCard(this.projects[i], i, cx, cy, list);
    }
    list.setContentHeight(Math.ceil(count / COLS) * (CARD_H + GAP_Y));
  }

  private levelCard(p: CreatorProject, i: number, cx: number, cy: number, list: ScrollList): void {
    const frame = this.add
      .rectangle(cx, cy, CARD_W, CARD_H, CREATOR_PANEL, 1)
      .setStrokeStyle(3, CREATOR_TEAL)
      .setInteractive({ useHandCursor: true })
      .setName(`creator-project-${i}`);
    const thumb = projectThumb(this, p, CARD_W - 20, 150);
    thumb.setPosition(cx, cy - 50);
    const name = this.add
      .text(cx, cy + 46, p.name, { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const del = this.deleteButton(cx, cy, i);
    list.content.add([frame, thumb, name, del]);
    const pub = this.published.get(p.id);
    list.content.add(
      this.publishControls(pub, i, cx, cy + 100, list, {
        publish: () =>
          this.askPublish(
            p.name,
            `"${p.name}"`,
            () => this.levelReady(p),
            () => publishLevel(accountClient()!, p.id),
          ),
        cancel: () => cancelLevelRequest(accountClient()!, p.id),
        ask: () => askUnban(accountClient()!, p.id),
      }),
    );
    frame.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (list.dragged || !list.inView(ptr) || this.confirm.length > 0) return;
      if (isLocked(pub)) {
        creatorFlash(this, LOCKED_TEXT);
        return;
      }
      this.scene.start('CreatorEditorScene', { id: p.id });
    });
    this.wireDelete(del, list, p.id, pub, () => creatorStoreNow().then((s) => s.remove(p.id)));
  }

  private chapterCard(
    c: CreatorChapter,
    i: number,
    cx: number,
    cy: number,
    list: ScrollList,
  ): void {
    const border = findBorder(c.border).color;
    const frame = this.add
      .rectangle(cx, cy, CARD_W, CARD_H, CREATOR_PANEL, 1)
      .setStrokeStyle(5, border)
      .setInteractive({ useHandCursor: true })
      .setName(`creator-chapter-${i}`);
    const name = this.add
      .text(cx, cy - 92, c.name, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const lines = c.levels
      .slice(0, 3)
      .map((code, n) => `${chapterLevelLabel(c.format, c.order, n)}  #${code}`);
    if (c.levels.length > 3) lines.push(`… ${c.levels.length - 3} more`);
    const body = this.add
      .text(cx, cy - 10, lines.length ? lines.join('\n') : 'No levels yet', {
        fontSize: '16px',
        color: '#c8d2d8',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5);
    const count = this.add
      .text(cx, cy + 62, `${c.levels.length} ${c.levels.length === 1 ? 'level' : 'levels'}`, {
        fontSize: '16px',
        color: '#9aa6ad',
      })
      .setOrigin(0.5)
      .setName(`creator-chapter-count-${i}`);
    const del = this.deleteButton(cx, cy, i, 'creator-chapter-delete');
    list.content.add([frame, name, body, count, del]);
    const pub = this.publishedChapters.get(c.id);
    list.content.add(
      this.publishControls(
        pub,
        i,
        cx,
        cy + 100,
        list,
        {
          publish: () =>
            this.askPublish(
              c.name,
              `The chapter "${c.name}"`,
              () => (c.levels.length > 0 ? null : 'Add a level to your chapter first'),
              () => publishChapter(accountClient()!, c.id),
            ),
          cancel: () => cancelChapterRequest(accountClient()!, c.id),
          ask: () => askUnbanChapter(accountClient()!, c.id),
        },
        'creator-chapter',
      ),
    );
    frame.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (list.dragged || !list.inView(ptr) || this.confirm.length > 0) return;
      if (isLocked(pub)) {
        creatorFlash(this, LOCKED_TEXT);
        return;
      }
      this.scene.start('CreatorChapterScene', { id: c.id });
    });
    this.wireDelete(del, list, c.id, pub, async () => {
      const r = await deleteChapter(accountClient()!, c.id);
      if (!r.ok) throw new Error(r.error);
    });
  }

  private deleteButton(cx: number, cy: number, i: number, prefix = 'creator-delete') {
    return this.add
      .text(cx + CARD_W / 2 - 20, cy - CARD_H / 2 + 20, '✕', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#b23a3a',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setName(`${prefix}-${i}`);
  }

  private wireDelete(
    del: Phaser.GameObjects.Text,
    list: ScrollList,
    id: string,
    pub: PublishedRow | undefined,
    remove: () => Promise<unknown>,
  ): void {
    del.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (list.dragged || !list.inView(ptr)) return;
      if (publishState(pub) === 'banned' || isLocked(pub)) {
        creatorFlash(this, "A banned level can't be deleted");
        return;
      }
      if (this.armedDelete !== id) {
        this.armedDelete = id;
        del.setText('DELETE?');
        return;
      }
      void remove()
        .then(() => this.loadData())
        .catch((e: unknown) => creatorFlash(this, e instanceof Error ? e.message : 'Try again'));
    });
  }

  /** The control row under a card (spec 2026-11-02 §4.1). */
  private publishControls(
    row: PublishedRow | undefined,
    i: number,
    cx: number,
    y: number,
    list: ScrollList,
    calls: { publish: () => void; cancel: Call; ask: Call },
    prefix = 'creator',
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const label = (x: number, text: string, color: string, name: string) => {
      const t = this.add
        .text(x, y, text, { fontSize: '17px', color, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setName(name);
      out.push(t);
      return t;
    };
    const button = (
      x: number,
      text: string,
      name: string,
      fill: number,
      w: number,
      onUp: () => void,
    ) => {
      const b = creatorButton(
        this,
        x,
        y,
        text,
        { name, width: w, height: 34, fontSize: 16, fill, depth: 2 },
        () => undefined,
      );
      b.bg.off('pointerup');
      b.bg.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (list.dragged || !list.inView(ptr) || this.busy || this.confirm.length > 0) return;
        onUp();
      });
      out.push(b.bg, b.text);
    };
    if (this.deviceOnly || !accountClient()) {
      label(cx, SIGN_IN_TO_PUBLISH, '#9aa6ad', `${prefix}-publish-signin-${i}`);
      return out;
    }
    switch (publishState(row)) {
      case 'none':
        button(cx, 'PUBLISH', `${prefix}-publish-${i}`, CREATOR_TEAL, 150, calls.publish);
        break;
      case 'pending':
        label(cx - 30, 'PENDING', '#ffd23f', `${prefix}-pending-${i}`);
        button(
          cx + 50,
          '✕',
          `${prefix}-cancel-${i}`,
          0xb23a3a,
          36,
          () => void this.act(calls.cancel),
        );
        break;
      case 'live':
        label(cx - 55, `LIVE #${row!.code}`, '#7ddb7d', `${prefix}-live-${i}`);
        button(cx + 80, 'UPDATE', `${prefix}-update-${i}`, CREATOR_TEAL, 100, calls.publish);
        break;
      case 'banned':
        label(cx - 40, 'BANNED', '#ff6b6b', `${prefix}-banned-${i}`);
        button(
          cx + 50,
          'ASK',
          `${prefix}-ask-${i}`,
          CREATOR_ORANGE,
          70,
          () => void this.act(calls.ask),
        );
        break;
      case 'asked':
        label(cx - 30, 'BANNED · ASKED', '#ff6b6b', `${prefix}-asked-${i}`);
        button(
          cx + 95,
          '✕',
          `${prefix}-cancel-${i}`,
          0xb23a3a,
          36,
          () => void this.act(calls.cancel),
        );
        break;
    }
    return out;
  }

  private levelReady(p: CreatorProject): string | null {
    return p.waves.length === 0 || onMapPlots(p).length === 0
      ? 'Your level needs a wave and a plot first'
      : null;
  }

  /** PUBLISH / UPDATE: the readiness check, then "Are you sure?". */
  private askPublish(_name: string, what: string, ready: () => string | null, call: Call): void {
    const notReady = ready();
    if (notReady) {
      creatorFlash(this, notReady);
      return;
    }
    const objs = this.confirm;
    const close = () => {
      for (const o of objs.splice(0)) o.destroy();
    };
    objs.push(
      this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.6).setDepth(50).setInteractive(),
      this.add
        .rectangle(640, 360, 560, 260, CREATOR_PANEL, 1)
        .setStrokeStyle(3, CREATOR_TEAL)
        .setDepth(51),
      this.add
        .text(640, 290, PUBLISH_CONFIRM, { fontSize: '32px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(52)
        .setName('creator-confirm-title'),
      this.add
        .text(
          640,
          348,
          `${what} will be checked by an admin, then\nappear in Explore with your username.`,
          { fontSize: '17px', color: '#c8d2d8', align: 'center' },
        )
        .setOrigin(0.5)
        .setDepth(52),
    );
    const ok = creatorButton(
      this,
      560,
      425,
      'OK',
      { name: 'creator-confirm-ok', width: 130, height: 50, depth: 52 },
      () => {
        close();
        void this.act(call);
      },
    );
    const cancel = creatorButton(
      this,
      720,
      425,
      'CANCEL',
      { name: 'creator-confirm-cancel', width: 130, height: 50, depth: 52, fill: 0x5c6168 },
      close,
    );
    objs.push(ok.bg, ok.text, cancel.bg, cancel.text);
  }

  /** Run one publishing call, then redraw from the server. */
  private async act(call: Call): Promise<void> {
    if (this.busy || !accountClient()) return;
    this.busy = true;
    const r = await call();
    this.busy = false;
    if (!this.sys.isActive()) return;
    if (!r.ok) creatorFlash(this, r.error ?? 'Try again');
    await (this.tab === 'levels' ? this.loadPublished() : this.loadChapters());
    if (this.sys.isActive()) this.render();
  }
}
